/**
 * Meta (Facebook) Pixel Tracking Service for Web
 * 
 * Platform-aware service that:
 * - Only runs on web (checks for window.fbq)
 * - Waits for the pixel to load before tracking
 * - Provides methods for CompleteRegistration and Purchase events
 * - Includes AEM (Advanced Event Matching) parameters for campaign optimization
 */

/**
 * Check if Facebook Pixel (fbq) is available
 * @returns {Promise<boolean>}
 */
const checkFbqAvailable = () => {
  if (typeof window === 'undefined') {
    console.log(
      '%c[Meta Pixel] ⚠️ Window not available',
      'background: #ff9800; color: white; padding: 4px 8px; border-radius: 4px; font-weight: bold;'
    );
    return Promise.resolve(false);
  }
  
  // If fbq is already available, return immediately
  if (typeof window.fbq === 'function') {
    console.log(
      '%c[Meta Pixel] ✅ Pixel Loaded (fbq available)',
      'background: #2196F3; color: white; padding: 4px 8px; border-radius: 4px; font-weight: bold;'
    );
    return Promise.resolve(true);
  }
  
  // Wait for fbq to load (max 5 seconds)
  console.log(
    '%c[Meta Pixel] ⏳ Waiting for pixel to load...',
    'background: #ff9800; color: white; padding: 4px 8px; border-radius: 4px; font-weight: bold;'
  );
  
  return new Promise((resolve) => {
    const startTime = Date.now();
    const checkInterval = setInterval(() => {
      if (typeof window.fbq === 'function') {
        clearInterval(checkInterval);
        console.log(
          '%c[Meta Pixel] ✅ Pixel Loaded Successfully',
          'background: #4CAF50; color: white; padding: 4px 8px; border-radius: 4px; font-weight: bold;',
          `\n⏱️ Load time: ${Date.now() - startTime}ms`
        );
        resolve(true);
      } else if (Date.now() - startTime > 5000) {
        clearInterval(checkInterval);
        console.warn(
          '%c[Meta Pixel] ❌ Pixel not loaded after 5 seconds',
          'background: #f44336; color: white; padding: 4px 8px; border-radius: 4px; font-weight: bold;'
        );
        resolve(false);
      }
    }, 100);
  });
};

/**
 * Track CompleteRegistration event
 * This event is fired when a new user creates an account
 * Required for AEM (Advanced Event Matching) campaigns
 * 
 * @param {string} method - Registration method: 'email', 'google', or 'apple'
 */
export const trackCompleteRegistration = async (method = 'email') => {
  try {
    console.log(
      '%c[Meta Pixel] 🔄 Attempting to track CompleteRegistration...',
      'background: #2196F3; color: white; padding: 4px 8px; border-radius: 4px; font-weight: bold;',
      `\n📝 Method: ${method.toUpperCase()}`
    );
    
    const isAvailable = await checkFbqAvailable();
    if (!isAvailable) {
      console.error(
        '%c[Meta Pixel] ❌ CompleteRegistration Event NOT Fired - Pixel not available',
        'background: #f44336; color: white; padding: 4px 8px; border-radius: 4px; font-weight: bold;'
      );
      return;
    }
    
    const eventParams = {
      registration_method: method,
      content_name: 'Account Registration',
      content_category: 'user_signup'
    };
    
    window.fbq('track', 'CompleteRegistration', eventParams);
    
    console.log(
      '%c[Meta Pixel] ✅✅✅ CompleteRegistration Event FIRED! ✅✅✅',
      'background: #4CAF50; color: white; padding: 8px 12px; border-radius: 4px; font-weight: bold; font-size: 14px;',
      '\n━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━',
      `\n🎉 EVENT: CompleteRegistration`,
      `\n📝 Method: ${method.toUpperCase()}`,
      `\n📦 Parameters:`,
      eventParams,
      `\n⏰ Timestamp: ${new Date().toISOString()}`,
      `\n━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━`
    );
  } catch (error) {
    console.error(
      '%c[Meta Pixel] ❌ Error tracking CompleteRegistration',
      'background: #f44336; color: white; padding: 4px 8px; border-radius: 4px; font-weight: bold;',
      error
    );
  }
};

/**
 * Track Purchase event
 * This event is fired when a user completes a purchase through RevenueCat
 * Required for AEM (Advanced Event Matching) campaigns
 * 
 * @param {Object} purchaseData - Purchase information
 * @param {number} purchaseData.amount - Purchase amount (in dollars, not cents)
 * @param {string} purchaseData.currency - Currency code (e.g., 'USD')
 * @param {string} purchaseData.productId - Product identifier
 * @param {string} purchaseData.transactionId - Transaction identifier (for deduplication)
 * @param {string} [purchaseData.contentName] - Product name/description
 * @param {string} [purchaseData.contentCategory] - Product category (default: 'subscription')
 */
export const trackPurchase = async (purchaseData) => {
  try {
    console.log(
      '%c[Meta Pixel] 🔄 Attempting to track Purchase...',
      'background: #2196F3; color: white; padding: 4px 8px; border-radius: 4px; font-weight: bold;',
      '\n📦 Purchase Data:',
      purchaseData
    );
    
    const isAvailable = await checkFbqAvailable();
    if (!isAvailable) {
      console.error(
        '%c[Meta Pixel] ❌ Purchase Event NOT Fired - Pixel not available',
        'background: #f44336; color: white; padding: 4px 8px; border-radius: 4px; font-weight: bold;'
      );
      return;
    }
    
    const {
      amount,
      currency,
      productId,
      transactionId,
      contentName,
      contentCategory = 'subscription'
    } = purchaseData;
    
    // AEM-required parameters for proper conversion tracking
    // These help Facebook match events for Aggregated Event Measurement
    const eventParams = {
      // Standard content parameters
      content_type: 'product',
      content_ids: [productId],
      content_name: contentName || productId,
      content_category: contentCategory,
      
      // Transaction identifier (required for deduplication)
      transaction_id: transactionId || productId,
      
      // Value and currency (required for purchase events)
      value: amount,
      currency: currency || 'USD',
      
      // Additional metadata for better matching
      num_items: 1
    };
    
    window.fbq('track', 'Purchase', eventParams);
    
    console.log(
      '%c[Meta Pixel] ✅✅✅ Purchase Event FIRED! ✅✅✅',
      'background: #4CAF50; color: white; padding: 8px 12px; border-radius: 4px; font-weight: bold; font-size: 14px;',
      '\n━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━',
      `\n💰 EVENT: Purchase`,
      `\n💵 Amount: ${currency || 'USD'} ${amount.toFixed(2)}`,
      `\n📦 Product ID: ${productId}`,
      `\n🆔 Transaction ID: ${transactionId || 'N/A'}`,
      `\n📝 Product Name: ${contentName || productId}`,
      `\n📂 Category: ${contentCategory}`,
      `\n📦 Parameters:`,
      eventParams,
      `\n⏰ Timestamp: ${new Date().toISOString()}`,
      `\n━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━`
    );
  } catch (error) {
    console.error(
      '%c[Meta Pixel] ❌ Error tracking Purchase',
      'background: #f44336; color: white; padding: 4px 8px; border-radius: 4px; font-weight: bold;',
      error
    );
  }
};

/**
 * Debug function to test Meta Pixel availability
 * Can be called from browser console: window.testMetaPixel()
 */
export const testMetaPixel = async () => {
  console.log(
    '%c[Meta Pixel] 🔍 Testing Meta Pixel...',
    'background: #2196F3; color: white; padding: 8px 12px; border-radius: 4px; font-weight: bold; font-size: 14px;',
    '\n━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━'
  );
  
  const isAvailable = await checkFbqAvailable();
  
  if (isAvailable) {
    console.log(
      '%c[Meta Pixel] ✅✅✅ PIXEL IS WORKING! ✅✅✅',
      'background: #4CAF50; color: white; padding: 8px 12px; border-radius: 4px; font-weight: bold; font-size: 14px;',
      '\n━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━',
      '\n✅ fbq function is available',
      '\n✅ Pixel is loaded and ready',
      '\n✅ Events can be tracked',
      '\n━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━'
    );
    
    // Test fire a test event
    try {
      window.fbq('track', 'PageView');
      console.log(
        '%c[Meta Pixel] ✅ Test PageView event fired successfully',
        'background: #4CAF50; color: white; padding: 4px 8px; border-radius: 4px; font-weight: bold;'
      );
    } catch (error) {
      console.error(
        '%c[Meta Pixel] ❌ Error firing test event',
        'background: #f44336; color: white; padding: 4px 8px; border-radius: 4px; font-weight: bold;',
        error
      );
    }
  } else {
    console.error(
      '%c[Meta Pixel] ❌❌❌ PIXEL IS NOT WORKING ❌❌❌',
      'background: #f44336; color: white; padding: 8px 12px; border-radius: 4px; font-weight: bold; font-size: 14px;',
      '\n━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━',
      '\n❌ fbq function is not available',
      '\n❌ Pixel may not be loaded',
      '\n❌ Events cannot be tracked',
      '\n━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━'
    );
  }
  
  return isAvailable;
};

// Make test function available globally for easy console testing
if (typeof window !== 'undefined') {
  window.testMetaPixel = testMetaPixel;
}

/**
 * Meta Tracking Service Object (for consistency with iOS implementation)
 */
const MetaTrackingService = {
  trackCompleteRegistration,
  trackPurchase,
  checkFbqAvailable,
  testMetaPixel
};

export default MetaTrackingService;

